import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { ActionIcon, Badge } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useMemo } from 'react';

import { UploadedFile } from 'src/Components/Shared/UploadedFile';
import { VideoPlayer } from 'src/Components/Video/VideoPlayer';
import { FormAttachment } from 'src/Hooks/Forms/types/formAttachment.types';
import { AssetStorageItemDtoTypeEnum } from 'src/Services/API/generated';

import styles from './FormAttachmentViewer.module.scss';
import { Props } from './FormAttachmentViewer.types';
import { SortableAttachment } from './SortableAttachment';

const FormAttachmentViewer = ({
  attachments,
  onDeleteAttachment,
  firstBadge,
  onReorder,
}: Props) => {
  const attachementsWithPreview = useMemo(
    () => attachments.filter(attachment => attachment.previewUrl),
    [attachments]
  );
  const attachementsWithoutPreview = useMemo(
    () =>
      attachments.filter(
        attachment =>
          attachment.previewUrl === undefined &&
          attachment.type === AssetStorageItemDtoTypeEnum.File &&
          !attachment.video
      ),
    [attachments]
  );
  const videoAttachments = useMemo(
    () => attachments.filter(attachment => !!attachment.video),
    [attachments]
  );

  // A small distance/delay so a click on the remove button or a scroll is not a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!onReorder || !over || active.id === over.id) return;
    onReorder(
      arrayMove(
        attachments,
        attachments.findIndex(attachment => attachment.fileId === active.id),
        attachments.findIndex(attachment => attachment.fileId === over.id)
      )
    );
  };

  const showsBadge = (attachment: FormAttachment) =>
    !!firstBadge && attachments.length > 1 && attachment.fileId === attachments[0]?.fileId;

  const thumbnail = (attachment: FormAttachment) => (
    <>
      <img
        src={attachment.previewUrl}
        className={styles.image}
        draggable={onReorder ? false : undefined}
      />
      {showsBadge(attachment) && (
        <Badge size="xs" radius="sm" className={styles.badge}>
          {firstBadge}
        </Badge>
      )}
      <ActionIcon
        radius={'xl'}
        color={'#0e5b61'}
        className={styles.removeImage}
        // The drag sensors listen to pointerdown: keep the click on the button.
        onPointerDown={onReorder ? event => event.stopPropagation() : undefined}
        onClick={() => {
          onDeleteAttachment(attachment);
        }}
      >
        <IconX style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
    </>
  );

  const previews = onReorder ? (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={attachementsWithPreview.map(attachment => attachment.fileId!)}
        strategy={rectSortingStrategy}
      >
        {attachementsWithPreview.map(attachment => (
          <SortableAttachment
            key={attachment.fileId}
            id={attachment.fileId!}
            className={styles.imageContainer}
          >
            {thumbnail(attachment)}
          </SortableAttachment>
        ))}
      </SortableContext>
    </DndContext>
  ) : (
    attachementsWithPreview.map(attachment => (
      <div key={attachment.fileId} className={styles.imageContainer}>
        {thumbnail(attachment)}
      </div>
    ))
  );

  return (
    <div className={styles.container}>
      {previews}

      {attachementsWithoutPreview.map(attachment => (
        <UploadedFile
          key={attachment.fileId}
          name={attachment.name}
          onDelete={() => onDeleteAttachment(attachment)}
        />
      ))}

      {videoAttachments.map(video => {
        if (video.video) {
          return <VideoPlayer key={video.fileId} video={video.video} />;
        }
      })}
    </div>
  );
};

export default FormAttachmentViewer;
